
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RFP, RFPEstimate } from "@/types/project-types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export function useRFPData() {
  const fetchRFPs = async () => {
    const { data, error } = await supabase
      .from('rfps')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  };

  const { data: rfps = [], isLoading, error, refetch: fetchRFPsRefresh } = useQuery({
    queryKey: ['rfps'],
    queryFn: fetchRFPs,
  });

  return { rfps, isLoading, error, fetchRFPs: fetchRFPsRefresh };
}

export function useSingleRFPData(rfpId: string | undefined) {
  const queryClient = useQueryClient();

  const fetchRFPData = async () => {
    if (!rfpId) return { rfp: null, estimates: [] };
    
    // Fetch RFP details
    const { data: rfpData, error: rfpError } = await supabase
      .from('rfps')
      .select('*')
      .eq('id', rfpId)
      .maybeSingle();

    if (rfpError) throw rfpError;
    
    // Fetch RFP estimates
    const { data: estimatesData, error: estimatesError } = await supabase
      .from('rfp_estimates')
      .select('*')
      .eq('rfp_id', rfpId);

    if (estimatesError) throw estimatesError;
    
    return { 
      rfp: rfpData, 
      estimates: estimatesData || [] 
    };
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['rfp', rfpId],
    queryFn: fetchRFPData,
    enabled: !!rfpId,
  });

  const updateRFPMutation = useMutation({
    mutationFn: async (updates: Partial<RFP>) => {
      if (!rfpId) throw new Error("RFP ID is required");
      
      const { data, error } = await supabase
        .from('rfps')
        .update(updates)
        .eq('id', rfpId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (newRFPData) => {
      queryClient.setQueryData(['rfp', rfpId], (oldData: any) => {
        return {
          ...oldData,
          rfp: newRFPData
        };
      });
    },
  });

  const updateEstimatesMutation = useMutation({
    mutationFn: async (newEstimates: RFPEstimate[]) => {
      if (!rfpId) throw new Error("RFP ID is required");
    
      console.log("Starting to update estimates for RFP:", rfpId);
      console.log("Estimates to save:", newEstimates);
    
      // Delete existing estimates
      const { error: deleteError } = await supabase
        .from('rfp_estimates')
        .delete()
        .eq('rfp_id', rfpId);

      if (deleteError) {
        console.error("Error deleting existing estimates:", deleteError);
        throw deleteError;
      }

      // Prepare estimates for insertion by cleaning them
      if (newEstimates.length > 0) {
        // Clean up the estimates by removing unnecessary fields
        const cleanEstimates = newEstimates.map(est => {
          // Use destructuring to exclude unwanted fields
          const { id, created_at, updated_at, ...cleanEstimate } = est;
          
          return {
            ...cleanEstimate,
            rfp_id: rfpId
          };
        });
        
        console.log("Clean estimates to insert:", cleanEstimates);
        
        const { data, error: insertError } = await supabase
          .from('rfp_estimates')
          .insert(cleanEstimates)
          .select();

        if (insertError) {
          console.error("Error inserting estimates:", insertError);
          throw insertError;
        }
        
        console.log("Successfully saved estimates:", data);
        return data || [];
      }
      
      return [];
    },
    onSuccess: (newEstimates) => {
      queryClient.setQueryData(['rfp', rfpId], (oldData: any) => {
        return {
          ...oldData,
          estimates: newEstimates
        };
      });
      
      console.log("Query cache updated with new estimates");
    },
    onError: (error) => {
      console.error("Error in updateEstimates mutation:", error);
    }
  });

  const updateRFP = async (updates: Partial<RFP>) => {
    try {
      return await updateRFPMutation.mutateAsync(updates);
    } catch (err) {
      console.error('Error updating RFP:', err);
      throw err;
    }
  };

  const updateEstimates = async (newEstimates: RFPEstimate[]) => {
    try {
      return await updateEstimatesMutation.mutateAsync(newEstimates);
    } catch (err) {
      console.error('Error updating estimates:', err);
      throw err;
    }
  };

  return { 
    rfp: data?.rfp || null, 
    estimates: data?.estimates || [], 
    isLoading, 
    error, 
    fetchRFPData: refetch, 
    updateRFP, 
    updateEstimates,
    isUpdatingRFP: updateRFPMutation.isPending,
    isUpdatingEstimates: updateEstimatesMutation.isPending
  };
}
